/**
 * AmountsStrip — the first thing on the lean surface (spec §3.1).
 *
 * Amounts read in BUILD-UP ORDER: initial advance (incl. any financed reserve and
 * closing costs) → + construction holdback →
 * = total loan amount, with the total captioned as what the guideline minimum
 * and maximum measure. That caption is not decoration: the owner ruling of
 * 2026-09-02 moved BOTH the floor and the ceiling onto the total, because a
 * $0-at-close construction deal drawing $2.4M is a $2.4M loan.
 *
 * Three states, one chip:
 *   live   — the declared client-side mirror, recomputed as the form changes
 *   priced — the backend's own derived amounts, after a run
 *   stale  — priced, but the form has drifted since; the numbers no longer
 *            describe what is on screen
 */
import Box from "@mui/material/Box";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { SZ } from "../../sizersShared/sizerUi";
import { fmtMoney, fmtRateDec } from "../../sizersShared/sizerFormat";
import { rtlRequiredCoupon } from "../../sizersShared/rtlRateDisplay";

const STATES = {
  live: { label: "Live estimate", bg: SZ.warnBg, fg: SZ.warn },
  priced: { label: "Priced", bg: SZ.goodBg, fg: SZ.good },
  stale: { label: "Stale — form changed", bg: SZ.badBg, fg: SZ.bad },
};

const Pill = ({ bg, fg, children }) => (
  <Txt
    sx={{
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: ".04em",
      textTransform: "uppercase",
      lineHeight: 1.45,
      alignSelf: "center",
      p: "3px 9px",
      borderRadius: "99px",
      background: bg,
      color: fg,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </Txt>
);

const Line = ({ op, label, value, total }) => (
  <Flx
    jb
    g={1}
    sx={{
      alignItems: "baseline",
      py: total ? "10px" : "7px",
      borderTop: total ? `1px solid ${SZ.line}` : "none",
      mt: total ? "4px" : 0,
    }}
  >
    <Flx ac g={0.75} sx={{ minWidth: 0 }}>
      <Box
        component="span"
        sx={{
          fontFamily: SZ.mono,
          fontSize: 12,
          width: 10,
          color: SZ.ink400,
          flexShrink: 0,
        }}
      >
        {op}
      </Box>
      <Txt
        sx={{
          fontSize: total ? 13 : 12.5,
          fontWeight: total ? 700 : 400,
          color: total ? SZ.ink900 : SZ.ink500,
        }}
      >
        {label}
      </Txt>
    </Flx>
    <Box
      component="span"
      sx={{
        fontFamily: SZ.mono,
        fontVariantNumeric: "tabular-nums",
        fontSize: total ? 16 : 13,
        fontWeight: total ? 700 : 500,
        color: SZ.ink900,
      }}
    >
      {fmtMoney(value)}
    </Box>
  </Flx>
);

export default function AmountsStrip({
  amounts,
  state = "live",
  result,
  onExplain,
}) {
  const s = STATES[state] || STATES.live;
  const rate = result?.rate || {};
  const coupon = rate.coupon ?? rtlRequiredCoupon(rate);

  return (
    <Box
      sx={{
        bgcolor: SZ.card,
        border: `1px solid ${SZ.line}`,
        borderRadius: SZ.radius,
        boxShadow: SZ.shadow,
        overflow: "hidden",
      }}
    >
      <Flx
        jb
        ac
        g={1}
        sx={{ p: "13px 17px", borderBottom: `1px solid ${SZ.line}`, flexWrap: "wrap" }}
      >
        <Txt
          sx={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: ".08em",
            textTransform: "uppercase",
            color: SZ.ink400,
          }}
        >
          Loan amount
        </Txt>
        <Pill bg={s.bg} fg={s.fg}>
          {s.label}
        </Pill>
      </Flx>

      <Box sx={{ p: "10px 17px 14px" }}>
        <Line op="" label="Initial advance" value={amounts?.advance} />
        {(amounts?.reserve > 0 || amounts?.closingCosts > 0) && (
          <Txt component="div" sx={{ fontSize: 11, color: SZ.ink400, mt: "-4px", mb: "2px", pl: "16px" }}>
            Includes{" "}
            {[
              amounts.reserve > 0 ? `${fmtMoney(amounts.reserve)} financed interest reserve` : null,
              amounts.closingCosts > 0 ? `${fmtMoney(amounts.closingCosts)} financed closing costs` : null,
            ]
              .filter(Boolean)
              .join(" and ")}
          </Txt>
        )}
        <Line op="+" label="Construction holdback" value={amounts?.holdback} />
        <Line op="=" label="Total loan amount" value={amounts?.total} total />
        <Txt component="div" sx={{ fontSize: 11, color: SZ.ink400, mt: "2px" }}>
          What the guideline minimum and maximum measure.
        </Txt>
        {amounts?.overridden && (
          <Txt component="div" sx={{ fontSize: 11, color: SZ.warn, mt: "6px" }}>
            The advance is a set amount, not the requested leverage.
          </Txt>
        )}
      </Box>

      {result && (
        <Flx
          jb
          ac
          g={1}
          sx={{
            p: "11px 17px",
            borderTop: `1px solid ${SZ.line}`,
            background: "#fbfcfc",
            flexWrap: "wrap",
          }}
        >
          <Flx ac g={0.75} sx={{ flexWrap: "wrap" }}>
            {coupon != null && (
              <Txt sx={{ fontSize: 12.5, color: SZ.ink500 }}>
                Coupon{" "}
                <Box
                  component="span"
                  sx={{
                    fontFamily: SZ.mono,
                    fontVariantNumeric: "tabular-nums",
                    fontWeight: 600,
                    color: SZ.ink900,
                  }}
                >
                  {fmtRateDec(coupon)}
                </Box>
              </Txt>
            )}
            <Pill
              bg={result.eligible === false ? SZ.badBg : SZ.goodBg}
              fg={result.eligible === false ? SZ.bad : SZ.good}
            >
              {result.eligible === false ? "Not eligible" : "Eligible"}
            </Pill>
            <Pill
              bg={result.lockReady ? SZ.goodBg : "#eef1f2"}
              fg={result.lockReady ? SZ.good : SZ.ink500}
            >
              {result.lockReady ? "Lock ready" : "Not lock ready"}
            </Pill>
          </Flx>
          <Box
            component="button"
            type="button"
            onClick={onExplain}
            sx={{
              border: `1px solid ${SZ.lineStrong}`,
              background: SZ.card,
              color: SZ.ink700,
              borderRadius: SZ.radiusSm,
              px: "13px",
              height: 30,
              fontSize: 12.5,
              fontWeight: 600,
              fontFamily: "inherit",
              cursor: "pointer",
              whiteSpace: "nowrap",
              "&:hover": {
                borderColor: SZ.teal500,
                color: SZ.teal700,
                background: SZ.teal50,
              },
            }}
          >
            Explain this result
          </Box>
        </Flx>
      )}
    </Box>
  );
}
